import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const CONSENT_KEY = "bricos-cookie-consent";

interface ConsentState {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp: string;
}

export const CookieConsent = () => {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) {
      // Show after short delay for better UX
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (consent: ConsentState) => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    setVisible(false);

    // Disable/enable PostHog based on analytics consent
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ph = (window as any).posthog as { opt_in_capturing?: () => void; opt_out_capturing?: () => void } | undefined;
    if (consent.analytics && ph) {
      ph.opt_in_capturing?.();
    } else if (ph) {
      ph.opt_out_capturing?.();
    }
  };

  const acceptAll = () => {
    saveConsent({
      essential: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString(),
    });
  };

  const acceptSelected = () => {
    saveConsent({
      essential: true,
      analytics,
      marketing,
      timestamp: new Date().toISOString(),
    });
  };

  const rejectAll = () => {
    saveConsent({
      essential: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString(),
    });
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[100] p-4 animate-in slide-in-from-bottom-4 duration-500">
      <div className="max-w-2xl mx-auto bg-card border border-border rounded-2xl shadow-2xl p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <Cookie className="w-6 h-6 text-primary shrink-0" />
            <h3 className="font-display font-bold text-foreground">
              Cookie Preferences
            </h3>
          </div>
          <button
            onClick={rejectAll}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-muted-foreground mb-4">
          We use cookies to provide essential functionality and improve your experience. 
          You can choose which cookies to accept.{" "}
          <Link to="/privacy" className="text-primary hover:underline">
            Privacy Policy
          </Link>
        </p>

        {showDetails && (
          <div className="space-y-3 mb-4 p-4 bg-muted/50 rounded-xl">
            <label className="flex items-center gap-3 cursor-not-allowed">
              <input type="checkbox" checked disabled className="rounded" />
              <div>
                <span className="text-sm font-medium text-foreground">Essential</span>
                <p className="text-xs text-muted-foreground">
                  Required for authentication, security, and basic functionality. Cannot be disabled.
                </p>
              </div>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={analytics}
                onChange={(e) => setAnalytics(e.target.checked)}
                className="rounded"
              />
              <div>
                <span className="text-sm font-medium text-foreground">Analytics</span>
                <p className="text-xs text-muted-foreground">
                  Help us understand how you use the platform to improve our service.
                </p>
              </div>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={marketing}
                onChange={(e) => setMarketing(e.target.checked)}
                className="rounded"
              />
              <div>
                <span className="text-sm font-medium text-foreground">Marketing</span>
                <p className="text-xs text-muted-foreground">
                  Personalized content and promotional communications.
                </p>
              </div>
            </label>
          </div>
        )}

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          {!showDetails ? (
            <>
              <Button onClick={acceptAll} className="flex-1">
                Accept All
              </Button>
              <Button variant="outline" onClick={() => setShowDetails(true)} className="flex-1">
                Customize
              </Button>
              <Button variant="ghost" onClick={rejectAll} className="flex-1 text-muted-foreground">
                Reject All
              </Button>
            </>
          ) : (
            <>
              <Button onClick={acceptSelected} className="flex-1">
                Save Preferences
              </Button>
              <Button variant="outline" onClick={acceptAll} className="flex-1">
                Accept All
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
